import { CrewButton } from 'components/elements/crewButton/crewButton'
import { memo, useCallback, useMemo } from 'react'
import { TaskEntryDialog } from 'features/task/components/taskEntryDialog/taskEntryDialog'
import EventAvailable from '~icons/material-symbols/event-available'
import { TaskSearch } from './components/taskSearch/taskSearch'
import { useTranslation } from '@crew/modules/i18n'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useModal } from 'components/layouts/modal/useModal'
import {
  GlobalMenuTaskDisplayModeButtonGroup,
  TaskDetailListTabs,
} from 'enums/app'
import { TaskEntryFormMode } from 'features/task/components/taskEntryDialog/components/taskEntryForm/taskEntryForm'
import { CrewButtonGroup } from 'components/elements/crewButtonGroup/crewButtonGroup'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { setSelectedGlobalMenuTaskListDisplayMode } from '../../states/taskListPageSlice'
import { CrewGuideToolTip } from 'components/devextreme/crewGuideToolTip/crewGuideToolTip'

type ButtonGroupInfo = {
  id: string
  text: string | undefined
  icon: JSX.Element | undefined
}

export const TaskListToolbar = memo(() => {
  const { t } = useTranslation()

  const { navigate } = useCrewNavigate()

  const dispatch = useAppDispatch()
  // 選択中のタスクリスト表示モードを取得
  const selectedTaskListDisplayMode = useAppSelector(
    (state) => state.taskListPage.selectedGlobalMenuTaskListDisplayMode
  )

  const [isTaskEntryDialogOpen, openTaskEntryDialog, closeTaskEntryDialog] =
    useModal()

  // タスク登録完了時
  const handleTaskRegistered = useCallback(
    (taskId: string) => {
      closeTaskEntryDialog()

      // タスク詳細画面に遷移
      // 遷移後のタブは関連情報タブとする
      navigate(`/tasks/${taskId}/${TaskDetailListTabs.RelatedInfo.value}`)
    },
    [closeTaskEntryDialog, navigate]
  )

  // 表示形式のボタングループ
  const taskDisplayModeButtonGroup: ButtonGroupInfo[] = useMemo(() => {
    const items = Object.keys(GlobalMenuTaskDisplayModeButtonGroup).map(
      (key) => {
        const item =
          GlobalMenuTaskDisplayModeButtonGroup[
            key as keyof typeof GlobalMenuTaskDisplayModeButtonGroup
          ]

        const SvgIcon = item.icon

        return {
          id: item.id,
          text: undefined,
          icon: (
            <div id={`${item.id}`}>
              <SvgIcon width={24} height={24} />
              {/* ツールチップ */}
              <CrewGuideToolTip
                target={`#${item.id}`}
                position="bottom"
                type={item.tooltipType}
              />
            </div>
          ),
        }
      }
    )

    return items
  }, [])

  // タスクリスト表示モードボタングループのアイテムクリック時
  const handleTaskDisplayModeItemClick = useCallback(
    (itemData: { id: string }) => {
      dispatch(setSelectedGlobalMenuTaskListDisplayMode(itemData.id))
    },
    [dispatch]
  )

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <div className="w-full flex gap-x-2.5 justify-between items-center">
          {/* ページタイトル */}
          <div className="flex gap-2 items-center h-8 text-crew-gray-3-light dark:text-crew-gray-2-dark">
            <EventAvailable width={24} height={24} />
            <p className="font-bold">{t('label.task') + t('label.list')}</p>
          </div>
          <div className="flex gap-x-1 ml-auto">
            {/* 新規タスクボタン */}
            <div className="flex flex-1">
              <CrewButton
                type="primary"
                className="ml-auto"
                text={t('action.newTask')}
                onClick={openTaskEntryDialog}
              />
            </div>
          </div>
        </div>
        <div>
          {/* 表示切替ボタングループ */}
          <CrewButtonGroup
            items={taskDisplayModeButtonGroup}
            keyExpr="id"
            textExpr="text"
            iconExpr="icon"
            stylingMode="text"
            selectedItemKey={selectedTaskListDisplayMode}
            onItemClick={handleTaskDisplayModeItemClick}
          />
        </div>

        {/* 検索条件 */}
        <div className="flex-1 flex gap-x-2.5 items-center">
          <TaskSearch />
        </div>
      </div>
      <TaskEntryDialog
        entryFormMode={TaskEntryFormMode.New}
        title={t('label.addTaskTitle')}
        onSubmit={handleTaskRegistered}
        isOpen={isTaskEntryDialogOpen}
        onClose={closeTaskEntryDialog}
      />
    </>
  )
})
